import React from 'react';
import {
  Box,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// ============================================================
// SubSection
// ============================================================
export function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box mb={2.5}>
      <Typography variant="body2" fontWeight={600} mb={1} sx={{ fontSize: 13, color: 'text.primary', letterSpacing: 0.1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

// ============================================================
// Markdown renderer
// ============================================================
export function MarkdownContent({ content }: { content: string }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        '& p': { mt: 0, mb: 1, lineHeight: 1.7, fontSize: 13, color: 'text.primary' },
        '& p:last-child': { mb: 0 },
        '& ul, & ol': { pl: 2, mb: 1, mt: 0 },
        '& li': { mb: 0.25, fontSize: 13, lineHeight: 1.6 },
        '& li::marker': { color: 'text.disabled' },
        '& h1, & h2, & h3, & h4': { mt: 1.5, mb: 0.75, fontWeight: 600 },
        '& h3': { fontSize: 13.5 },
        '& h4': { fontSize: 13 },
        '& code': {
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          px: 0.6,
          py: 0.15,
          borderRadius: 0.5,
          fontSize: '0.82em',
          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
          color: 'primary.main',
        },
        '& pre': {
          bgcolor: theme.palette.mode === 'dark' ? 'grey.50' : 'grey.900',
          p: 1.5,
          borderRadius: 2,
          overflow: 'auto',
          mb: 1.5,
          fontSize: 12,
          '& code': { bgcolor: 'transparent', px: 0, py: 0, color: '#e2e8f0' },
        },
        '& blockquote': {
          borderLeft: '2px solid',
          borderColor: alpha(theme.palette.primary.main, 0.2),
          pl: 1.5,
          ml: 0,
          color: 'text.secondary',
          fontStyle: 'italic',
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          mb: 1.5,
          '& th, & td': {
            border: '1px solid',
            borderColor: 'divider',
            px: 1.25,
            py: 0.5,
            fontSize: 12.5,
          },
          '& th': { bgcolor: alpha(theme.palette.primary.main, 0.03), fontWeight: 600 },
        },
        '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
        '& hr': { borderColor: 'divider', my: 1.5, opacity: 0.5 },
        '& strong': { fontWeight: 600 },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </Box>
  );
}

// ============================================================
// Key-value display block
// ============================================================
export function KeyValueBlock({ items }: { items: { label: string; value?: string | null }[] }) {
  const theme = useTheme();
  const filtered = items.filter((f) => f.value);
  if (filtered.length === 0) return null;
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      {filtered.map((f, i) => (
        <Box key={i} sx={{
          display: 'flex', px: 1.5, py: 0.75,
          borderBottom: i < filtered.length - 1 ? '1px solid' : 'none',
          borderColor: 'divider',
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
          transition: 'background 0.15s',
        }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', width: 180, flexShrink: 0 }}>
            {f.label}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.primary', wordBreak: 'break-word' }}>{f.value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ============================================================
// Findings and recommendations display
// ============================================================
export function FindingsAndRecs({ findings, recommendations, findingsLabel = 'Findings' }: { findings?: string[]; recommendations?: string[]; findingsLabel?: string }) {
  if (!findings?.length && !recommendations?.length) return null;
  return (
    <Box mt={1.5}>
      {findings && findings.length > 0 && (
        <Box mb={1.5}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'warning.main', mb: 0.5 }}>{findingsLabel} ({findings.length})</Typography>
          <MarkdownContent content={findings.map((f: string) => `- ${f}`).join('\n')} />
        </Box>
      )}
      {recommendations && recommendations.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'info.main', mb: 0.5 }}>Recommendations ({recommendations.length})</Typography>
          <MarkdownContent content={recommendations.map((r: string) => `- ${r}`).join('\n')} />
        </Box>
      )}
    </Box>
  );
}

// ============================================================
// Risk level chip
// ============================================================
export function RiskChip({ level }: { level: string }) {
  const color = level.toLowerCase() === 'high' ? '#ef4444' : level.toLowerCase() === 'medium' ? '#f59e0b' : level.toLowerCase() === 'critical' ? '#dc2626' : '#22c55e';
  return (
    <Box sx={{
      display: 'inline-block', px: 0.75, py: 0.1, borderRadius: 1,
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
      bgcolor: alpha(color, 0.1), color, letterSpacing: 0.3,
    }}>
      {level}
    </Box>
  );
}
